import React, { useContext, useEffect, useRef, useState } from "react"
import { View, StyleSheet, Text, Image, ScrollView } from "react-native"
import * as amplitude from "@amplitude/analytics-react-native"
import moment from "moment"
import "moment/locale/fr"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { theme } from "@common/theme/theme"
import {
    FUNNEL_TITLE_FONT_SIZE,
    FUNNEL_TITLE_LINE_HEIGHT,
} from "@common/components/funnel/FunnelQuestionScreen"
import { DimensionsContext } from "@common/utils/mobileUtils"
import appPreview from "@assets/offers/app-preview.png"
import BrevetFunnelHeader from "../brevet/components/BrevetFunnelHeader"
import FunnelEmbeddedCheckout from "../brevet/components/FunnelEmbeddedCheckout"
import { FunnelCheckoutFaqSection } from "@common/components/funnel/FunnelCheckoutFaqSection"
import {
    FunnelPriceCard,
    FunnelPriceDetail,
    FunnelRecapCard,
    FunnelRecapLine,
} from "@common/components/funnel/FunnelCheckoutCards"
import FilledButtonM from "@common/components/button/FilledButtonM"
import { NewSubscriptionGuaranteesCard } from "../new-subscription/v2/components/NewSubscriptionGuaranteesCard"
import { useSummerFunnel } from "./context/SummerFunnelContext"
import { useSummerFunnelGuard } from "./hooks/useSummerFunnelGuard"
import { summerFunnelService } from "./services/summerFunnelService"
import {
    SUMMER_SLOTS,
    SUMMER_DEFAULT_SLOT_IDS,
    SUMMER_MONTHS,
    resolveSummerPricing,
    weeklyHoursFromSlots,
    capitalize,
} from "./constants/summerContent"
import SummerStartPicker from "./components/SummerStartPicker"
import SummerHelpBanner from "./components/SummerHelpBanner"
import { useSummerStartOptions } from "./hooks/useSummerStartOptions"
import { summerSecondFullMonth } from "./utils/summerStartPricing"
import { formatEuros } from "../brevet/utils/formatEuros"

moment.locale("fr")

// Même largeur de colonne de contenu que les écrans de question (F2 :
// homogénéité du langage visuel — seule l'illustration diffère).
const CONTENT_MAX = 480

// Récap garanties — 2 items distincts seulement (lean checkout). Forme attendue
// par NewSubscriptionGuaranteesCard : { icon, label, subtitle }. Deux jeux selon la
// variante servie par le lien fast-track : le 2ᵉ item bascule « sans engagement »
// (défaut) ↔ « tarif engagement 12 mois » quand le lien sert la grille engagement.
const CHECKOUT_GUARANTEES_NO_COMMITMENT = [
    {
        icon: "calendar-check",
        label: "Annulable sans condition avant le début des cours",
        subtitle: "Vous changez d'avis ? Aucun frais, aucune question.",
    },
    {
        icon: "shield-alt",
        label: "Sans engagement, résiliable à tout moment",
        subtitle: "À la rentrée, vous choisissez de continuer une formule régulière ou d'arrêter.",
    },
]

const CHECKOUT_GUARANTEES_COMMITMENT = [
    {
        icon: "piggy-bank",
        label: "Le tarif d'été le plus avantageux",
        subtitle: "En contrepartie d'un engagement 12 mois, vous bénéficiez du meilleur tarif pour les cours d'été.",
    },
]

// Joint des labels en français : ["Juillet"] → "juillet" ; ["Juillet","Août"]
// → "juillet–août" (récap compact en minuscules).
const joinMonthsCompact = (labels) => {
    const lower = labels.map((l) => l.toLowerCase())
    if (lower.length <= 1) return lower[0] || ""
    return `${lower[0]}–${lower[lower.length - 1]}`
}

/**
 * Étape paiement 1 € — RÉCAP LEAN. Le programme a été révélé à l'étape juste
 * avant (reveal) ; les créneaux / mois / matières / prix ont été choisis aux
 * étapes amont. Ici, une seule chose à faire : payer 1 € pour bloquer la place.
 *
 * Contenu (sobre, anti-redite) :
 *  1. Titre « Réservez la place de {prénom} » + « Dernière étape… 1 € ».
 *  2. Visuel produit (mockup app) en haut, pour projeter le parent dans le produit.
 *  3. UNE ligne de conviction (les créneaux sont limités).
 *  4. Récap compact 1 ligne du choix + « Modifier » → configurateur.
 *  5. Prix en un bloc clair : Aujourd'hui 1 € → échéancier daté, sans répéter
 *     le tarif mensuel/horaire déjà explicité sur la page Formule.
 *  6. Un seul bloc commercial « Réservez en confiance » : pourquoi 1 € + garanties.
 *  7. PaymentElement Stripe (source unique des logos réseaux officiels) + bloc
 *     sécurité juste après + CTA fixe allégé.
 *
 * On NE re-liste PAS les créneaux/mois/matières en grand (déjà au configurateur),
 * ni le programme (déjà au reveal), ni un témoignage/second pavé sécurisé.
 *
 * Tarif piloté par le lien fast-track (variante engagement servie via
 * `with_commitment`, défaut SANS engagement). Au « Réserver », Stripe confirme le
 * 1 € et crée l'abonnement cours_ete à démarrage différé. En variante « sans 1 € »
 * (`skipDeposit`), pas de formulaire Stripe : on souscrit directement sur la carte
 * déjà au dossier via `subscribe-on-file` (0 € aujourd'hui).
 */
const SummerSlotsCheckoutScreen = ({ navigation }) => {
    const dimensions = useContext(DimensionsContext)
    const { state, update } = useSummerFunnel()
    // Refresh-safe / URL-partageable : un refresh / une URL ouverte ailleurs sur
    // le checkout ré-hydrate via summer/resume (sinon « Session expirée »).
    const { userParam, nextParams } = useSummerFunnelGuard()

    const [config, setConfig] = useState(null)
    const [clientSecret, setClientSecret] = useState(null)
    const [creatingSession, setCreatingSession] = useState(false)
    const [sessionError, setSessionError] = useState(null)
    // Chemin « sans 1 € » : confirmation directe (souscription sur la carte au dossier).
    const [confirming, setConfirming] = useState(false)
    const [confirmError, setConfirmError] = useState(null)

    // Créneaux choisis au configurateur (fallback : la pré-sélection par défaut).
    // Source du prix, du programme, du démarrage et de la checkout session.
    const slotIds = state.selectedSlotIds?.length
        ? state.selectedSlotIds
        : SUMMER_DEFAULT_SLOT_IDS
    const chosenSlots = SUMMER_SLOTS.filter((s) => slotIds.includes(s.id))
    // Pack d'heures hebdo = nombre de créneaux choisis (chaque créneau = 1 h) → clé du palier.
    const weeklyHours = weeklyHoursFromSlots(slotIds)

    // Programme — re-fetché ici (mémoïsé backend par profile_key, peu coûteux)
    // uniquement pour alimenter les matières du récap compact + synchroniser
    // firstSession (la date réelle du lead). Pas de calendrier affiché ici.
    const [programSubjects, setProgramSubjects] = useState([])
    // Catalogue matières de la classe — source ROBUSTE des libellés du récap :
    // résout la sélection stockée (state.subjects → noms) indépendamment du
    // programme. Le récap matières ne dépend ainsi plus du gate hasProgramPayload
    // (un lead repris sans objectif/niveau garde son récap matières affiché).
    const [catalogSubjects, setCatalogSubjects] = useState([])

    // Le program endpoint exige le payload complet (matières + objectif + niveau
    // + classes + créneaux), sinon 422. Un lead repris en `summer_checkout_created`
    // dont les checkout_params n'ont pas de matières arriverait ici avec
    // state.subjects vide → 422 (avalé, mais pollue la console). On garde l'appel
    // derrière la même vérification que le reveal (SummerPlanScreen).
    const hasProgramPayload =
        (state.subjects?.length || 0) > 0 &&
        !!state.objective &&
        !!state.niveauActuel &&
        !!state.studentClass &&
        !!state.studentClassRentree &&
        slotIds.length > 0

    useEffect(() => {
        amplitude.track("summer_funnel_slots_viewed", { funnel_state_id: state.funnelStateId })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Récupère la config (window_start + first_session_hour) → fallback de date
    // de 1ʳᵉ séance / prélèvement si le programme ne renvoie pas first_session.
    useEffect(() => {
        if (!state.funnelStateId) return
        let cancelled = false
        summerFunnelService
            .getFunnelState({ stateId: state.funnelStateId })
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? {}
                setConfig(payload.config ?? null)
            })
            .catch(() => {
                /* fallback générique conservé si la config ne charge pas */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.funnelStateId])

    // Catalogue matières de la classe (même source que le configurateur) → noms du
    // récap résolus depuis la sélection stockée, sans dépendre du programme.
    useEffect(() => {
        if (!state.studentClass) return
        let cancelled = false
        summerFunnelService
            .getSubjects({ studentClass: state.studentClass })
            .then((res) => {
                if (cancelled) return
                const list = res?.data?.data ?? res?.data ?? []
                setCatalogSubjects(Array.isArray(list) ? list : [])
            })
            .catch(() => {
                /* récap retombe sur programSubjects si le catalogue ne charge pas */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.studentClass])

    // Programme — matières du récap + synchro firstSession (date réelle,
    // honorant la date de démarrage choisie).
    useEffect(() => {
        if (!state.funnelStateId) return
        // Payload incomplet (ex. reprise sans matières) → on ne tire pas l'appel
        // (il finirait en 422) ; le récap matières est simplement omis.
        if (!hasProgramPayload) return
        let cancelled = false
        summerFunnelService
            .getProgram(
                { stateId: state.funnelStateId },
                {
                    subjects: state.subjects,
                    objective: state.objective,
                    niveauActuel: state.niveauActuel,
                    studentClass: state.studentClass,
                    studentClassRentree: state.studentClassRentree,
                    hoursPerWeek: weeklyHours || 3,
                    slotIds,
                    subjectHours: state.subjectHours,
                    months: state.selectedMonths,
                    startDate: state.startDate,
                }
            )
            .then((res) => {
                if (cancelled) return
                const payload = res?.data?.data ?? res?.data ?? null
                setProgramSubjects(payload?.subjects || [])
                if (payload?.first_session) {
                    update({ firstSession: payload.first_session })
                }
            })
            .catch(() => {
                /* récap matières omis si l'appel échoue ; le reste reste valable */
            })
        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.funnelStateId, hasProgramPayload, state.startDate])

    // Prix mensuel = fonction du PACK D'HEURES (somme des durées) ET de la variante
    // d'engagement (servie par le lien fast-track admin ; défaut SANS engagement). La
    // grille API (config) prime ; la grille constante en fallback.
    const pricing = resolveSummerPricing(
        weeklyHours,
        !!state.withCommitment,
        config?.pricing_grid
    )
    // Garanties : le 2ᵉ item suit la variante d'engagement servie par le lien.
    const guarantees = state.withCommitment
        ? CHECKOUT_GUARANTEES_COMMITMENT
        : CHECKOUT_GUARANTEES_NO_COMMITMENT

    // Récap compact — matières · jours+heure · mois.
    const monthIds = state.selectedMonths?.length
        ? state.selectedMonths
        : SUMMER_MONTHS.map((m) => m.id)
    const monthsLabel = joinMonthsCompact(
        SUMMER_MONTHS.filter((m) => monthIds.includes(m.id)).map((m) => m.label)
    )
    // Matières du récap : le programme (ordre pédagogique) en premier quand il est
    // généré ; SINON on retombe sur la sélection STOCKÉE (state.subjects → noms via
    // le catalogue de la classe). Plus de disparition silencieuse quand
    // hasProgramPayload est faux (lead repris sans objectif/niveau/classe-rentrée).
    const subjectNameById = new Map(
        catalogSubjects.map((s) => [String(s.id), s.name])
    )
    const selectedSubjectNames = (state.subjects || [])
        .map((id) => subjectNameById.get(String(id)))
        .filter(Boolean)
    const subjectsLabel = programSubjects.length
        ? programSubjects.map((s) => s.name).join(", ")
        : selectedSubjectNames.join(", ")
    // Créneaux choisis : jour abrégé (3 lettres) + horaire — iso avec le
    // configurateur (« Lun 10h30-11h30 »). Un jour peut avoir matin ET soir.
    const slotsLabel = chosenSlots
        .map((s) => `${s.label.slice(0, 3)} ${s.time}`)
        .join(", ")

    // Options de date de démarrage (backend, jamais recalculées ici). Le choix
    // pilote la 1ʳᵉ séance ET le prorata de la 1ʳᵉ facture, prélevée ce jour-là.
    const {
        options: startOptions,
        selectedOption: selectedStartOption,
        monthlyPriceCents: startMonthlyPriceCents,
        failed: startOptionsFailed,
    } = useSummerStartOptions({
        stateId: state.funnelStateId,
        slotIds,
        months: state.selectedMonths,
        withCommitment: !!state.withCommitment,
        startDate: state.startDate,
    })

    // Mesure du rendu dégradé : paiement affiché (le parent peut payer) alors
    // que les options — donc le prorata — n'ont pas chargé → le bloc prix
    // retombe sur le plein tarif générique. Invisible jusqu'ici (cas prod du
    // 07/07 : plein tarif affiché, prélèvement réel proraté). Échec avéré →
    // event immédiat ; sinon délai de grâce (les deux fetchs — session Stripe
    // et options — partent en parallèle au montage : options simplement plus
    // lentes de quelques centaines de ms ≠ rendu dégradé).
    const fallbackTrackedRef = useRef(false)
    useEffect(() => {
        if (fallbackTrackedRef.current) return
        if (!clientSecret || startOptions.length) return
        const track = () => {
            fallbackTrackedRef.current = true
            amplitude.track("summer_funnel_checkout_price_fallback_rendered", {
                funnel_state_id: state.funnelStateId,
                start_options_failed: !!startOptionsFailed,
            })
        }
        if (startOptionsFailed) {
            track()
            return
        }
        const graceTimer = setTimeout(track, 4000)
        return () => clearTimeout(graceTimer)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientSecret, startOptions.length, startOptionsFailed])

    const onSelectStartOption = (option) => {
        amplitude.track("summer_funnel_start_option_selected", {
            funnel_state_id: state.funnelStateId,
            start_date: option.start_date,
        })
        update({
            startDate: option.start_date,
            firstChargeAmountCents: option.first_charge_amount_cents,
            firstSession: option.first_session,
        })
    }

    // 1ʳᵉ séance réelle du lead : l'option choisie prime, sinon la date propagée
    // par le programme, sinon la config générique.
    const firstSessionIso =
        selectedStartOption?.first_session ||
        state.firstSession ||
        config?.first_session ||
        null

    // Démarrage : jour de la 1ʳᵉ séance ("lundi 6 juillet") — fallback affiché
    // seulement quand les options n'ont pas chargé (le picker prend le relais sinon).
    const startLabel = firstSessionIso
        ? capitalize(moment(firstSessionIso).locale("fr").format("dddd D MMMM"))
        : "à la 1ʳᵉ semaine de juillet"

    // 1ᵉʳ prélèvement = LE JOUR DE LA 1ʳᵉ SÉANCE, montant proratisé sur les
    // séances restantes du mois d'entrée (source backend). Fallback générique
    // tant que les options n'ont pas chargé.
    const firstChargeLabel = selectedStartOption
        ? moment(selectedStartOption.start_date).locale("fr").format("D MMMM")
        : firstSessionIso
          ? moment(firstSessionIso).locale("fr").format("D MMMM")
          : "au démarrage des cours"
    const firstChargeAmount = selectedStartOption
        ? formatEuros(selectedStartOption.first_charge_amount_cents / 100)
        : null
    // « au prorata » seulement quand le 1ᵉʳ mois est réellement partiel.
    const firstChargeIsProrated =
        selectedStartOption &&
        selectedStartOption.first_charge_amount_cents < Math.round(pricing.price * 100)
    // Cours d'été = JUILLET + AOÛT uniquement : il n'y a jamais de prélèvement
    // récurrent au-delà de l'édition. Une 2ᵉ échéance n'existe que si le mois
    // suivant l'entrée est un mois d'édition RÉELLEMENT CHOISI — un août plein
    // pour une entrée en juillet. Jamais pour une entrée en août (mois suivant =
    // septembre, hors édition), ni pour un lead juillet-only.
    //
    // Dérivé de l'option CHOISIE (pas de `recurring_start_date`, figé au fetch) :
    // le picker permet désormais de basculer sur une date d'août sans re-requête,
    // et la valeur d'API resterait bloquée sur le « 1er août » de l'entrée juillet
    // par défaut → 2ᵉ échéance fantôme. Même règle que le récap du picker.
    const editionMonths = state.selectedMonths?.length
        ? state.selectedMonths
        : [7, 8]
    const recurringStart = summerSecondFullMonth(selectedStartOption, editionMonths)
    const showSecondMonthCharge = !!recurringStart
    const recurringStartLabel = recurringStart
        ? recurringStart.locale("fr").format("Do MMMM")
        : null

    const containerWidth = Math.min(dimensions?.width || 400, CONTENT_MAX)
    const firstname = state.studentFirstname || "votre enfant"

    // Le checkout s'adapte à DEUX axes servis par le lien fast-track : le dépôt
    // (1 € vs « sans 1 € » sur la carte au dossier) et l'engagement (« sans
    // condition / sans frais » ne vaut PAS avec un engagement 12 mois).
    const skipDeposit = !!state.skipDeposit
    const todayAmountLabel = skipDeposit ? "0 €" : "1 €"
    const checkoutSubtitle = skipDeposit
        ? `Dernière étape — confirmez l'inscription de ${firstname}.`
        : "Dernière étape — on bloque les créneaux pour 1 €."
    const whyTitle = skipDeposit
        ? "Aucun paiement aujourd'hui"
        : "Pourquoi 1 € aujourd'hui ?"
    const whyLead = skipDeposit
        ? `Votre carte déjà enregistrée n'est débitée qu'au démarrage des cours, le ${firstChargeLabel}.`
        : `Pour bloquer la place de ${firstname} dans son groupe (places limitées). Le tarif des cours n'est prélevé qu'au démarrage, le ${firstChargeLabel}.`
    const commercialReassuranceItems = [
        {
            icon: "lock",
            label: whyTitle,
            subtitle: whyLead,
        },
        ...guarantees,
    ]

    // FAQ checkout — backend-driven. The screen selects from both variants using
    // the current local choice, so an engagement URL cannot race the GET that
    // precedes persistence of with_commitment. `faq` remains the back-compat fallback.
    const faqVariantKey = state.withCommitment
        ? "commitment_12m"
        : "no_commitment"
    const checkoutFaqs = config?.faq_variants?.[faqVariantKey] ?? config?.faq ?? []

    // Création de la checkout session embarquée : encaisse le 1 € aujourd'hui et
    // ancre l'abonnement mensuel (prix selon créneaux). (Re)créée à chaque
    // changement de la sélection effective (créneaux / mois / engagement) — voir
    // les deps : la session DOIT suivre ce que le récap affiche, sinon on facture
    // le palier figé au montage (souvent le défaut 3 créneaux) au lieu du choix réel.
    useEffect(() => {
        setClientSecret(null)
        // Chemin sans 1 € → pas de Checkout Session (souscription directe sur le PM).
        if (state.skipDeposit) return
        if (!state.funnelStateId) {
            setSessionError("Session expirée — reprenez depuis le début.")
            return
        }
        if (!slotIds.length) return

        let cancelled = false
        setCreatingSession(true)
        setSessionError(null)
        summerFunnelService
            .createCheckoutSession(
                { stateId: state.funnelStateId },
                {
                    slot_ids: slotIds,
                    months: state.selectedMonths,
                    with_commitment: !!state.withCommitment,
                    // Date de démarrage choisie — persistée en checkout_params ;
                    // le webhook la re-valide contre les options vivantes.
                    ...(state.startDate ? { start_date: state.startDate } : {}),
                }
            )
            .then((res) => {
                if (cancelled) return
                const secret =
                    res?.data?.data?.client_secret ?? res?.data?.client_secret
                if (secret) {
                    setClientSecret(secret)
                } else {
                    setSessionError("Impossible d'initialiser le paiement. Réessayez.")
                }
            })
            .catch(() => {
                if (cancelled) return
                setSessionError("Impossible d'initialiser le paiement. Réessayez.")
            })
            .finally(() => {
                if (!cancelled) setCreatingSession(false)
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        state.funnelStateId,
        state.skipDeposit,
        // Sélection effective : re-crée la session (et écrase checkout_params côté
        // serveur) quand le parent modifie ses créneaux/mois/engagement APRÈS le
        // montage, ou quand la vraie sélection s'hydrate après coup (reprise /
        // sessionStorage). Sérialisé en chaîne pour des deps stables (pas d'identité
        // de tableau qui changerait à chaque rendu).
        slotIds.join(","),
        (state.selectedMonths || []).join(","),
        state.withCommitment,
        state.startDate,
    ])

    const onPaymentSuccess = (paymentIntentId) => {
        amplitude.track("summer_funnel_payment_succeeded", {
            funnel_state_id: state.funnelStateId,
            slot_ids: slotIds,
        })
        // Fige la date + le prorata affichés pour l'écran de confirmation (même
        // si le parent n'a jamais touché le picker).
        update({
            paid: true,
            paymentIntentId,
            startDate: selectedStartOption?.start_date || state.startDate,
            firstChargeAmountCents:
                selectedStartOption?.first_charge_amount_cents ??
                state.firstChargeAmountCents,
            firstSession: selectedStartOption?.first_session || state.firstSession,
        })
        navigation.navigate("summer/confirmation", nextParams)
    }

    const onPaymentError = (errorCode) => {
        amplitude.track("summer_funnel_payment_failed", {
            funnel_state_id: state.funnelStateId,
            error_code: errorCode || "unknown",
        })
    }

    // Chemin « sans 1 € » : souscrit directement sur la carte au dossier (0 €
    // aujourd'hui). 422 `no_payment_method` → on retombe sur le flux 1 € (qui
    // enregistre la carte) en remettant skipDeposit à false.
    const onConfirmNoDeposit = () => {
        if (!state.funnelStateId) {
            setConfirmError("Session expirée — reprenez depuis le début.")
            return
        }
        setConfirming(true)
        setConfirmError(null)
        amplitude.track("summer_funnel_no_deposit_confirm_clicked", {
            funnel_state_id: state.funnelStateId,
        })
        summerFunnelService
            .subscribeOnFile(
                { stateId: state.funnelStateId },
                {
                    with_commitment: !!state.withCommitment,
                    ...(selectedStartOption?.start_date || state.startDate
                        ? { start_date: selectedStartOption?.start_date || state.startDate }
                        : {}),
                },
                userParam ? { user: userParam } : {}
            )
            .then((res) => {
                const ok =
                    res?.data?.data?.subscription_created ??
                    res?.data?.subscription_created
                if (ok) {
                    amplitude.track("summer_funnel_no_deposit_succeeded", {
                        funnel_state_id: state.funnelStateId,
                    })
                    update({
                        paid: true,
                        startDate: selectedStartOption?.start_date || state.startDate,
                        firstChargeAmountCents:
                            selectedStartOption?.first_charge_amount_cents ??
                            state.firstChargeAmountCents,
                        firstSession:
                            selectedStartOption?.first_session || state.firstSession,
                    })
                    navigation.navigate("summer/confirmation", nextParams)
                } else {
                    setConfirmError(
                        "Impossible de finaliser l'inscription. Réessayez."
                    )
                }
            })
            .catch((err) => {
                const code = err?.response?.data?.error
                const msg = err?.response?.data?.message
                if (code === "no_payment_method") {
                    // Pas de carte au dossier → bascule sur le flux 1 € (qui
                    // enregistre la carte).
                    setConfirmError(
                        msg ||
                            "Aucun moyen de paiement enregistré — la réservation se fait avec 1 €."
                    )
                    update({ skipDeposit: false })
                } else {
                    setConfirmError(
                        msg || "Impossible de finaliser l'inscription. Réessayez."
                    )
                }
            })
            .finally(() => setConfirming(false))
    }

    // Unique affordance d'édition : renvoie au configurateur (créneaux / mois /
    // prix). L'état est préservé par le contexte.
    const onEditChoices = () => {
        amplitude.track("summer_funnel_edit_choices_clicked", {
            funnel_state_id: state.funnelStateId,
        })
        navigation.navigate("summer/configurator", nextParams)
    }

    const summary = (
        <>
            <Text style={styles.title}>Réservez la place de {firstname}</Text>
            <Text style={styles.subtitle}>{checkoutSubtitle}</Text>

            {/* Visuel produit (mockup app) — projette le parent dans le produit. */}
            <View style={styles.previewBand}>
                <Image
                    source={appPreview}
                    style={styles.previewImage}
                    resizeMode="contain"
                />
            </View>

            {/* Récap : une ligne par dimension (matières / créneaux + h/sem / mois). */}
            <FunnelRecapCard onEdit={onEditChoices}>
                {subjectsLabel ? (
                    <FunnelRecapLine
                        icon="book"
                        label="Matières"
                        value={subjectsLabel}
                    />
                ) : null}
                <FunnelRecapLine
                    icon="calendar-week"
                    label="Créneaux"
                    value={`${slotsLabel} · ${weeklyHours} h/semaine`}
                />
                {monthsLabel ? (
                    <FunnelRecapLine icon="sun" label="Mois" value={monthsLabel} />
                ) : null}
            </FunnelRecapCard>

            {/* Prix en un bloc clair — carte partagée avec les autres funnels.
                Le 1 € domine ; dessous, le pattern prorata 1ᵉʳ mois (réf.
                InvoicePaymentStep) : montant proratisé le jour de la 1ʳᵉ
                séance, puis — seulement si un 2ᵉ mois d'édition est choisi —
                le mois plein daté. Aucune récurrence au-delà d'août.
                Le fallback dégradé (options non chargées) reste daté et neutre,
                sans « /mois » : cours d'été = juillet + août. */}
            <FunnelPriceCard
                todayValue={todayAmountLabel}
                thenLines={
                    firstChargeAmount
                        ? [
                              `puis ${firstChargeAmount} le ${firstChargeLabel}${
                                  firstChargeIsProrated
                                      ? " — pour toutes les séances restantes du mois"
                                      : " — pour le 1ᵉʳ mois complet"
                              }`,
                              showSecondMonthCharge && recurringStartLabel
                                  ? `puis ${formatEuros(pricing.price)} le ${recurringStartLabel}`
                                  : null,
                          ]
                        : [
                              `1ᵉʳ prélèvement le ${firstChargeLabel} — au prorata des séances restantes`,
                          ]
                }
            >
                {/* Début des cours — cliquable quand plusieurs options de
                    démarrage existent (le choix ajuste le prorata ci-dessus). */}
                {startOptions.length ? (
                    <SummerStartPicker
                        options={startOptions}
                        selectedOption={selectedStartOption}
                        onSelect={onSelectStartOption}
                        monthlyPriceCents={startMonthlyPriceCents}
                        chosenMonths={editionMonths}
                    />
                ) : (
                    <FunnelPriceDetail icon="calendar-alt">
                        Démarrage {startLabel}
                    </FunnelPriceDetail>
                )}
                <FunnelPriceDetail icon="video">
                    Cours en visio sur notre plateforme
                </FunnelPriceDetail>
            </FunnelPriceCard>

            {/* Un seul bloc commercial scannable : pourquoi 1 € + garanties. */}
            <View style={styles.guaranteesWrap}>
                <NewSubscriptionGuaranteesCard
                    title="Réservez en confiance"
                    items={commercialReassuranceItems}
                />
            </View>
        </>
    )

    return (
        <View style={styles.root}>
            <View style={[styles.frame, { width: containerWidth }]}>
                <BrevetFunnelHeader
                    step={7}
                    total={7}
                    hideLogo
                    onBack={() => navigation.goBack()}
                />
                {skipDeposit ? (
                    // Chemin sans 1 € : pas de formulaire Stripe — un récap + une CTA
                    // de confirmation qui souscrit sur la carte déjà au dossier.
                    <ScrollView contentContainerStyle={styles.noDepositScroll}>
                        {summary}
                        {confirmError ? (
                            <Text style={styles.confirmError}>{confirmError}</Text>
                        ) : null}
                        <FilledButtonM
                            label={`Confirmer l'inscription de ${firstname}`}
                            loading={confirming}
                            onPress={onConfirmNoDeposit}
                            style={{ width: null, marginTop: 8 }}
                            wrapText
                        />
                        <Text style={styles.noDepositReassurance}>
                            Aucun prélèvement aujourd'hui. 1ᵉʳ prélèvement
                            {firstChargeAmount ? ` de ${firstChargeAmount}` : ""} le{" "}
                            {firstChargeLabel} (jour de la 1ʳᵉ séance), sur la
                            carte déjà enregistrée.
                        </Text>
                        <FunnelCheckoutFaqSection faqs={checkoutFaqs} />
                        <SummerHelpBanner step={7} />
                    </ScrollView>
                ) : (
                    <FunnelEmbeddedCheckout
                        clientSecret={clientSecret}
                        summary={summary}
                        buttonLabel="Valider l'inscription · 1 €"
                        guarantees={[]}
                        secureBadge
                        belowContent={
                            <>
                                <FunnelCheckoutFaqSection faqs={checkoutFaqs} />
                                <SummerHelpBanner step={7} />
                            </>
                        }
                        onSuccess={onPaymentSuccess}
                        onError={onPaymentError}
                        creatingSession={creatingSession}
                        sessionError={sessionError}
                        containerWidth={containerWidth}
                        nativeNotice="Le paiement se finalise depuis un navigateur. Rouvrez le lien sur un ordinateur pour terminer votre réservation."
                    />
                )}
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    guaranteesWrap: { marginTop: 20 },

    // Chemin « sans 1 € » : récap scrollable + CTA de confirmation.
    noDepositScroll: { paddingBottom: 32 },
    confirmError: {
        fontFamily: "Montserrat-Bold",
        fontSize: 13,
        color: theme.colors.error,
        textAlign: "center",
        marginTop: 16,
    },
    root: { flex: 1, alignItems: "center", backgroundColor: theme.colors.whiteAlmost },
    frame: { flex: 1, backgroundColor: theme.colors.white, paddingHorizontal: 20 },
    // Titre calé sur la MÊME échelle typo que les écrans de question (F2).
    title: {
        fontFamily: "Montserrat-ExtraBold",
        fontSize: FUNNEL_TITLE_FONT_SIZE,
        lineHeight: FUNNEL_TITLE_LINE_HEIGHT,
        color: theme.colors.primaryDark || theme.colors.primary,
        marginBottom: 6,
    },
    subtitle: { fontFamily: "Montserrat-Regular", fontSize: 13, color: theme.colors.grey, marginBottom: 16 },

    // Visuel produit (mockup) en haut.
    previewBand: {
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.colors.primaryLighter,
        borderRadius: 16,
        paddingVertical: 10,
        marginBottom: 12,
    },
    previewImage: { width: "72%", height: 104 },

    // Récap, FAQ repliable et bloc prix vivent désormais dans
    // @common/components/funnel (FunnelCheckoutCards / FunnelCheckoutFaqSection).
    noDepositReassurance: {
        fontFamily: "Montserrat-Regular",
        fontSize: 12,
        color: theme.colors.grey,
        textAlign: "center",
        marginTop: 10,
        lineHeight: 16,
    },

})

export default SummerSlotsCheckoutScreen
